import { useEffect, useState } from 'react'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import CurrencyInput from 'components/form/CurrencyInput'
import CheckBox from 'components/form/CheckBox'
import {
  integerValue,
  invalidNumberInput,
  isEmptyObject,
  isNullOrUndefined,
} from 'utils'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  hideCampaignDailyBudgetEdit,
  hidePreview,
  setDailyBudgetYn,
  showPreview,
  updateCampaignsDailyBudget,
} from 'features/dashboard/operation/campaignDailyBudgetEdit/campaignDailyBudgetEditSlice'
import { Modal } from 'components/dialog/Modals'
import Table from 'components/table/Table'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import FixedTable from 'components/table/FixedTable'

const CampaignDailyBudgetEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    campaigns,
    isLoading,
    loadingError,
    form: { dailyBudgetYn, dailyBudgetAmount },
    preview: {
      dailyBudgetYn: previewDailyBudgetYn,
      dailyBudgetAmount: previewDailyBudgetAmount,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.campaignDailyBudgetEdit,
    shallowEqual
  )
  const [dailyBudgetAmountInput, setDailyBudgetAmountInput] =
    useState(dailyBudgetAmount)
  useEffect(() => {
    if (visible) {
      setDailyBudgetAmountInput(dailyBudgetAmount)
    }
  }, [visible, dailyBudgetAmount])
  const { errors, validate } = useValidator(
    () => ({
      dailyBudgetAmount:
        dailyBudgetYn === 'Y' &&
        (dailyBudgetAmountInput === '' ||
          invalidNumberInput(dailyBudgetAmountInput, 10000000, 1000, 10)) &&
        true,
    }),
    [dailyBudgetYn, dailyBudgetAmountInput]
  )

  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview(dailyBudgetAmountInput))
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateCampaignsDailyBudget())
        if (error) {
          preventClose()
          fail(
            '캠페인 일예산 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('캠페인 일예산 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(
    () => () => {
      dispatch(hideCampaignDailyBudgetEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(() => {
    if (loadingError) {
      dispatch(hideCampaignDailyBudgetEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          409: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const warningCount = campaigns.filter(
    ({
      dailyBudgetYn: beforeDailyBudgetYn,
      dailyBudgetAmount: beforeDailyBudgetAmount,
    }) => {
      const before =
        beforeDailyBudgetYn === 'N' ? undefined : beforeDailyBudgetAmount
      const after =
        previewDailyBudgetYn === 'N' ? undefined : previewDailyBudgetAmount
      const percentage =
        isNullOrUndefined(before) || isNullOrUndefined(after)
          ? 0
          : (after / before) * 100
      return percentage >= 500
    }
  ).length
  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    dailyBudgetYn !== previewDailyBudgetYn ||
    dailyBudgetAmountInput !== previewDailyBudgetAmount

  return (
    <Modal on={visible} className="campaignbudget_layer">
      <PreviewConfirmModal
        title="캠페인 일예산 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed || !preview}
        handleClose={() => dispatch(hideCampaignDailyBudgetEdit())}>
        <p className="desc_copy">
          하루 기준의 통합 지출 한도를 설정하며, 일 예산을 초과할 경우 광고
          집행이 중지됩니다.
        </p>
        <div className="group_budget">
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <strong className="tit_budget">일예산</strong>
              <div className="group_item">
                <CurrencyInput
                  min={1000}
                  max={10000000}
                  step={10}
                  maxLength={8}
                  value={dailyBudgetAmountInput}
                  disabled={dailyBudgetYn === 'N'}
                  error={errors.dailyBudgetAmount}
                  onValueChange={value =>
                    setDailyBudgetAmountInput(integerValue(value))
                  }
                />
                <CheckBox
                  label="미설정"
                  name="dailyBudgetYn"
                  checked={dailyBudgetYn === 'N'}
                  onCheckedChange={checked =>
                    dispatch(setDailyBudgetYn(checked ? 'N' : 'Y'))
                  }
                />
              </div>
              <p
                className={classNames({
                  desc_item: isNullOrUndefined(errors.dailyBudgetAmount),
                  txt_error: errors.dailyBudgetAmount,
                })}>
                캠페인 일예산은 최소 1,000원 ~ 최대 1천만원까지 10원 단위로
                설정할 수 있습니다.
              </p>
              {preview && (
                <>
                  <FixedTable caption="일예산 변경 이력 표">
                    <Table.Head>
                      <Table.Column
                        title="선택 캠페인"
                        className="inner_cell"
                        width={194}
                      />
                      <Table.Column
                        title="변경 전"
                        className="inner_cell"
                        width={171}
                      />
                      <Table.Column title="변경 후" className="inner_cell" />
                    </Table.Head>
                    <Table.Body>
                      {campaigns.map(
                        ({
                          id,
                          name,
                          dailyBudgetYn: beforeDailyBudgetYn,
                          dailyBudgetAmount: beforeDailyBudgetAmount,
                        }) => {
                          const before =
                            beforeDailyBudgetYn === 'N'
                              ? undefined
                              : beforeDailyBudgetAmount
                          const after =
                            previewDailyBudgetYn === 'N'
                              ? undefined
                              : previewDailyBudgetAmount
                          const percentage =
                            isNullOrUndefined(before) ||
                            isNullOrUndefined(after)
                              ? 0
                              : (after / before) * 100
                          const fixed = Math.ceil(percentage.toFixed(1))
                          return (
                            <Table.Row
                              rowId={id}
                              key={id}
                              className={classNames({
                                update: before !== after,
                              })}>
                              <td>
                                <div className="inner_cell">
                                  <span className="txt_limit2">{name}</span>
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {isNullOrUndefined(before)
                                    ? '미설정'
                                    : `${before.toLocaleString()}원`}
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {isNullOrUndefined(after)
                                    ? '미설정'
                                    : `${after.toLocaleString()}원`}
                                  {percentage >= 500 && (
                                    <span className="txt_error">
                                      {fixed.toLocaleString()}%로 증가폭이
                                      큽니다.
                                    </span>
                                  )}
                                </div>
                              </td>
                            </Table.Row>
                          )
                        }
                      )}
                    </Table.Body>
                  </FixedTable>
                  {warningCount > 0 && (
                    <p className="txt_error">
                      변경 전 금액 대비 증가폭이 큰 입력값이 {warningCount}개
                      존재합니다. 입력값이 맞는지 확인하세요.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </PreviewConfirmModal>
    </Modal>
  )
}
export default CampaignDailyBudgetEditModal
